/**
 * Hook for implementing pull-to-refresh functionality
 * Works only on mobile devices and/or PWA mode
 */

import { useEffect, useRef } from 'react';
import PullToRefresh from 'pulltorefreshjs';
import { useIsStandalone } from './useIsStandalone';
import { useMediaQuery } from 'react-responsive';

/**
 * Hook for pull-to-refresh functionality
 * @param {Object} options - Configuration options
 * @param {Function} options.onRefresh - Callback function when refresh is triggered
 * @param {string|Object} options.targetElement - CSS selector or ref object for the element to attach pull-to-refresh
 * @param {boolean} options.enabled - Enable/disable pull-to-refresh (default: true)
 */
export const usePullToRefresh = ({ 
  onRefresh, 
  targetElement = 'body',
  enabled = true
}) => {
  const isStandalone = useIsStandalone();
  const isMobile = useMediaQuery({ maxWidth: '800px' });
  const instanceRef = useRef(null);

  useEffect(() => {
    // Enable pull-to-refresh only for mobile or PWA
    const shouldEnable = enabled && (isMobile || isStandalone);

    if (!shouldEnable || !onRefresh) {
      // Clean up if disabled
      if (instanceRef.current) {
        try {
          instanceRef.current.destroy();
        } catch (e) {
          // Ignore errors
        }
        instanceRef.current = null;
      }
      return;
    }

    // Helper to get the actual element
    const getActualElement = () => {
      let element = targetElement;
      if (typeof element === 'string') {
        element = document.querySelector(element);
      } else if (element && element.current) {
        // It's a ref object
        element = element.current;
      }
      return element;
    };

    const actualElement = getActualElement();

    // Wait for element to be available
    if (!actualElement) {
      return;
    }

    // Destroy existing instance before creating a new one
    if (instanceRef.current) {
      try {
        instanceRef.current.destroy();
      } catch (e) {
        // Ignore errors
      }
      instanceRef.current = null;
    }

    // Find content element after header to insert pull-to-refresh before it
    const findContentElement = () => {
      // Try to find ScreenDiv or ContentDiv - the main content area after header
      const screenDiv = actualElement.querySelector('[class*="screen-div"]');
      if (screenDiv) {
        // Find first child after HeaderDiv (could be ContentDiv or other content)
        const headerDiv = screenDiv.querySelector('[class*="header-div"]');
        if (headerDiv && headerDiv.nextSibling) {
          return headerDiv.nextSibling;
        }
        // If no HeaderDiv, return first child of ScreenDiv
        return screenDiv.firstChild || screenDiv;
      }
      
      // Fallback: try to find ContentDiv directly
      const contentDiv = actualElement.querySelector('[class*="content-div"]');
      if (contentDiv) return contentDiv;
      
      // Last fallback: use actualElement itself
      return actualElement;
    };

    const contentElement = findContentElement();

    // Initialize PullToRefresh
    try {
      instanceRef.current = PullToRefresh.init({
        mainElement: contentElement, // Insert pull-to-refresh before content (under header)
        triggerElement: actualElement, // Scroll detection on the Shell container
        onRefresh: () => {
          return new Promise((resolve) => {
            const result = onRefresh();
            
            if (result && typeof result.then === 'function') {
              result
                .then(() => resolve())
                .catch(() => resolve());
            } else {
              resolve();
            }
          });
        },
        // Only allow pull-to-refresh when scrolled to the top
        shouldPullToRefresh: () => {
          let mainElement = actualElement;
          if (!mainElement) {
            const scrollTop = window.pageYOffset 
              || document.documentElement.scrollTop 
              || document.body.scrollTop 
              || 0;
            return scrollTop <= 5;
          }
          
          const scrollTop = mainElement.scrollTop || 0;
          return scrollTop <= 5;
        },
        // Visual options
        instructionsPullToRefresh: '\u200B', // Zero-width space (invisible)
        instructionsReleaseToRefresh: '...', // Show ellipsis when ready to release
        instructionsRefreshing: '...', // Show ellipsis when refreshing
        iconArrow: '\u200B', // Don't show arrow
        iconRefreshing: '&hellip;', // Use library default (ellipsis)
        // Distance thresholds
        distThreshold: 80,
        distMax: 100,
        distReload: 80,
        resistanceFunction: (t) => Math.min(1, t / 4.0),
      });

      // Simple text removal
      const removeTextContent = () => {
        const ptrContainers = document.querySelectorAll('[class*="ptr--"]');
        ptrContainers.forEach(ptrContainer => {
          if (!ptrContainer) return;
          
          // Hide text elements with zero-width space
          const textElements = ptrContainer.querySelectorAll('[class*="text"], [class*="instruction"]');
          textElements.forEach(el => {
            const text = el.textContent && el.textContent.trim();
            if (!text || text === '\u200B') {
              el.style.display = 'none';
            }
          });

          // Remove "true" text nodes
          const walker = document.createTreeWalker(
            ptrContainer,
            NodeFilter.SHOW_TEXT,
            null,
            false
          );

          let textNode = walker.nextNode();
          while (textNode) {
            const text = textNode.textContent.trim();
            if (text && text.toLowerCase() === 'true') {
              try {
                textNode.textContent = '';
              } catch (e) {
                // Ignore errors
              }
            }
            textNode = walker.nextNode();
          }
        });
      };

      removeTextContent();
      const intervalId = setInterval(removeTextContent, 500);
      instanceRef.current._textInterval = intervalId;

      // Simple z-index and background enforcement
      const enforceZIndex = () => {
        const pathname = window.location.pathname;
        const isGreenPage = pathname === '/' || pathname === '/profile';
        
        const ptrElements = document.querySelectorAll('[class*="ptr--"]');
        ptrElements.forEach(element => {
          if (element) {
            element.style.zIndex = '50';
            if (isGreenPage) {
              element.style.backgroundColor = '#00A898';
            }
          }
        });
      };

      enforceZIndex();
      const zIndexInterval = setInterval(enforceZIndex, 200);
      instanceRef.current._zIndexInterval = zIndexInterval;

    } catch (error) {
      console.error('Failed to initialize pull-to-refresh:', error);
    }

    // Cleanup on unmount
    return () => {
      if (instanceRef.current) {
        try {
          if (instanceRef.current._textInterval) {
            clearInterval(instanceRef.current._textInterval);
          }
          if (instanceRef.current._zIndexInterval) {
            clearInterval(instanceRef.current._zIndexInterval);
          }
          instanceRef.current.destroy();
        } catch (error) {
          console.error('Error destroying pull-to-refresh:', error);
        }
        instanceRef.current = null;
      }
    };
  }, [isMobile, isStandalone, enabled, onRefresh, targetElement]);

  return {
    isEnabled: enabled && (isMobile || isStandalone),
    destroy: () => {
      if (instanceRef.current) {
        try {
          if (instanceRef.current._textInterval) {
            clearInterval(instanceRef.current._textInterval);
          }
          if (instanceRef.current._zIndexInterval) {
            clearInterval(instanceRef.current._zIndexInterval);
          }
          instanceRef.current.destroy();
        } catch (e) {
          // Ignore errors
        }
        instanceRef.current = null;
      }
    }
  };
};
